// Gateway client for eatlete: Bearer token from localStorage, single-flight auto-refresh on
// 401 via /auth/refresh, hard logout when the refresh fails.
//
// Same shape as the admin panel's client on purpose — the gateway contract is
// identical, and two divergent implementations of "what does a 401 mean" is how
// one of them quietly stops refreshing.
import { destr } from 'destr'
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/constants/storage-keys'

/// Пара токенів, як її віддає наш auth. Оголошена тут, а не в спільних типах:
/// це єдине місце застосунку, яке взагалі знає про refresh.
type TokenPair = { accessToken: string; refreshToken: string }

export const API_URL = import.meta.env['VITE_API_URL'] ?? 'http://localhost:4000'

export const getAccessToken = (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY)
export const getRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY)

export const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

export const clearTokens = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

export class ApiError extends Error {
  readonly status: number
  /// Services answer with `data: { code }` — the only key the mobile clients
  /// decode, and the one worth branching on here too.
  readonly code: string | null

  constructor(status: number, message: string, code: string | null = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

export type Query = Record<string, string | number | boolean | null | undefined>

const buildUrl = (path: string, query?: Query): string => {
  const url = new URL(path, API_URL)
  Object.entries(query ?? {})
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .forEach(([key, value]) => url.searchParams.set(key, String(value)))
  return url.toString()
}

/// Response body → value, without a throw in the middle.
///
/// `destr` hands back the raw string when the body is not JSON, which is
/// exactly the case that matters here: a 502 from Caddy is an HTML page, and
/// `res.json()` would turn a bad-gateway into an unrelated SyntaxError.
const readBody = async <T>(res: Response): Promise<T> => {
  const raw = await res.text()
  return destr<T>(raw)
}

// ---- single-flight refresh ----

let refreshInFlight: Promise<string | null> | null = null

const requestRefresh = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null
  try {
    const res = await fetch(buildUrl('/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
    if (!res.ok) return null
    const pair = await readBody<TokenPair>(res)
    setTokens(pair.accessToken, pair.refreshToken)
    return pair.accessToken
  } catch {
    return null
  }
}

const refreshAccessToken = (): Promise<string | null> => {
  refreshInFlight ??= requestRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

/// Called when the session is beyond saving. A full page load, not a router
/// navigation: it also throws away every cached query, which is the point —
/// none of it belongs to whoever logs in next.
export const forceLogout = (): void => {
  clearTokens()
  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign('/login')
  }
}

export type ApiInit = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Query
  signal?: AbortSignal
  /** Skip the Authorization header and the 401-refresh dance (login / 2fa). */
  skipAuth?: boolean
}

const readError = async (res: Response): Promise<ApiError> => {
  const body = await readBody<{
    message?: string
    statusMessage?: string
    data?: { code?: string }
  }>(res)
  const parsed = typeof body === 'object' && body !== null ? body : null
  return new ApiError(
    res.status,
    parsed?.message ?? parsed?.statusMessage ?? `HTTP ${res.status}`,
    parsed?.data?.code ?? null,
  )
}

const rawFetch = async (path: string, init: ApiInit, allowRetry: boolean): Promise<Response> => {
  const token = init.skipAuth ? null : getAccessToken()
  const res = await fetch(buildUrl(path, init.query), {
    method: init.method ?? (init.body !== undefined ? 'POST' : 'GET'),
    headers: {
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    ...(init.signal ? { signal: init.signal } : {}),
  })

  if (res.status === 401 && !init.skipAuth) {
    if (!allowRetry) {
      forceLogout()
      throw new ApiError(401, 'Сесія завершена')
    }
    const newToken = await refreshAccessToken()
    if (!newToken) {
      forceLogout()
      throw new ApiError(401, 'Сесія завершена')
    }
    return rawFetch(path, init, false)
  }

  if (!res.ok) throw await readError(res)
  return res
}

/// JSON request against the gateway. Returns the parsed body (undefined for 204).
export const apiFetch = async <T>(path: string, init: ApiInit = {}): Promise<T> => {
  const res = await rawFetch(path, init, true)
  if (res.status === 204) return undefined as T
  return readBody<T>(res)
}

export const errorMessage = (err: unknown): string =>
  err instanceof ApiError ? err.message : 'Немає звʼязку із сервером'
